import {Button, Card, TextInput} from 'platform/components';
import {Grid, GridItem, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {getWorktreeName, setWorktreeOverride} from '@dms/config';
import {redirectToLogin} from '@dms/routes';

const MAIN_BACKEND_LABEL = 'main';

/**
 * Local-dev only: routes every API request to a backend worktree on the shared local stack, which
 * serves each worktree as its own app selected by the `X-Worktree` header.
 */
export function WorktreeSelector() {
  const currentWorktree = getWorktreeName();
  const [draft, setDraft] = useState<string | null>(currentWorktree);

  const switchTo = (worktreeName: string | null) => {
    if (setWorktreeOverride(worktreeName)) {
      redirectToLogin();
    }
  };

  return (
    <Card title="Backend worktree" variant="inlineGrey">
      <VStack spacing={2}>
        <Grid columns={4} align="flex-end">
          <GridItem span={3}>
            <TextInput
              label={`Worktree name (currently on ${currentWorktree ?? MAIN_BACKEND_LABEL})`}
              value={draft}
              onChange={setDraft}
              placeholder={`Empty for ${MAIN_BACKEND_LABEL}`}
              data-testid="devSettings-worktree-input"
            />
          </GridItem>

          <Button
            title="Switch"
            onClick={() => switchTo(isNilOrEmpty(draft) ? null : draft)}
            isDisabled={(isNilOrEmpty(draft) ? null : draft) === currentWorktree}
            leftIcon="action/swap_horiz"
            variant="secondary"
            data-testid="devSettings-worktree-switchButton"
          />
        </Grid>

        <Show when={!isNil(currentWorktree)}>
          <Button
            title={`Back to ${MAIN_BACKEND_LABEL}`}
            onClick={() => switchTo(null)}
            leftIcon="navigation/arrow_back"
            variant="secondary"
            data-testid="devSettings-worktree-clearButton"
          />
        </Show>

        <Text size="xSmall" color="tertiary">
          Routes every API request to a backend git worktree on the local stack (`X-Worktree`
          header). Switching logs you out — refresh tokens live in each worktree&apos;s own
          database. The worktree must be provisioned with `teas-mono worktree init` and `teas-mono
          fullstack on`, otherwise every request fails with a 500.
        </Text>
      </VStack>
    </Card>
  );
}
